import { Edge, Graph, PointLike } from "@antv/x6"
import { useCallback } from "react"
import { LineJumpType } from "../services/modal"
import { createBaseStream, createLineJump } from "../nodes"
import { getLineEndPoint } from "../services/NozzleEdgeLayout"


interface LineJumpOptions {
  edge: Edge
  point: PointLike
  type: LineJumpType
}
export function useAddLineJump() {
  
  return useCallback((graph: Graph, options: LineJumpOptions) => {
    const {edge, type, point} = options
    const vertices = edge.getVertices()
    const sourcePoint = getLineEndPoint(graph, edge, true)
    const targetPoint = getLineEndPoint(graph, edge, false)
    const edgeData = edge.getData();
    const allPoints = [sourcePoint, ...vertices, targetPoint]
    const info = projectPointToPolyline(point, allPoints)
    const nodeOptions = createLineJump(info.point, type, info.angle, {streamId: edgeData.streamId ?? edge.id})
    const jumpNode = graph.createNode(nodeOptions)
    const lines = breakEdge(graph, edge, point, info.segmentIndex)
    graph.addNode(jumpNode)
    const [startPort, endPort] = nodeOptions.ports.items
    const [line, newLine] = lines
    if (info.direction === LineDirection.LeftToRight) {
      line.setTarget({cell: jumpNode.id, port: startPort.id})
      newLine.setSource({cell: jumpNode.id, port: endPort.id})
    } else {
      line.setTarget({cell: jumpNode.id, port: endPort.id})
      newLine.setSource({cell: jumpNode.id, port: startPort.id})
    }
    
  }, [])
}

function breakEdge(graph: Graph, edge: Edge, point: PointLike, breakIndex: number) {
  const vertices: PointLike[] = edge.getVertices()
  const target = edge.getTarget()
  edge.setTarget(point)
  edge.setVertices(vertices.slice(0, breakIndex))
  const data = edge.getData();
  const newLineOption = createBaseStream(point, target as any, vertices.slice(breakIndex), {
    showTag: false,
    tag: data.tag,
    streamId: data.streamId || edge.id
  })
  const newEdge = graph.addEdge(graph.createEdge(newLineOption))
  return [edge, newEdge]
}

enum LineDirection {
  LeftToRight,
  RightToLeft
}
export type ProjectionResult = {
  point: PointLike         // the point in line
  distance: number
  segmentIndex: number     // segmentIndex (polyline[i] -> polyline[i+1])
  t: number                // the ratio in the segment [0,1]
  angle: number
  direction: LineDirection
}

function projectPointToSegment(p: PointLike, a: PointLike, b: PointLike) {
  const abx = b.x - a.x
  const aby = b.y - a.y
  const apx = p.x - a.x
  const apy = p.y - a.y

  const abLen2 = abx * abx + aby * aby
  if (abLen2 === 0) {
    const dx = p.x - a.x
    const dy = p.y - a.y
    return { point: { ...a }, t: 0, dist2: dx * dx + dy * dy }
  }

  let t = (apx * abx + apy * aby) / abLen2
  if (t < 0) t = 0
  else if (t > 1) t = 1

  const qx = a.x + t * abx
  const qy = a.y + t * aby

  const dx = p.x - qx
  const dy = p.y - qy

  return { point: { x: qx, y: qy }, t, dist2: dx * dx + dy * dy }
}

function projectPointToPolyline(p: PointLike, polyline: PointLike[]): ProjectionResult {
  if (polyline.length < 2) throw new Error("polyline not valid")

  let best: { point: PointLike; t: number; dist2: number } | null = null
  let bestIndex = -1

  for (let i = 0; i < polyline.length - 1; i++) {
    const a = polyline[i]
    const b = polyline[i + 1]
    const proj = projectPointToSegment(p, a, b)
    if (!best || proj.dist2 < best.dist2) {
      best = proj
      bestIndex = i
    }
  }

  const a = polyline[bestIndex]
  const b = polyline[bestIndex + 1]
  const direction = b.x - a.x >= 0 ? LineDirection.LeftToRight : LineDirection.RightToLeft
  const angleRad = Math.atan2(b.y - a.y, b.x - a.x)
  let deg = (angleRad * 180) / Math.PI;
  const upToDown = b.y - a.y >= 0;
  if (direction === LineDirection.RightToLeft && upToDown) {
    deg = 360 - Math.abs(deg)
  } else if (direction === LineDirection.RightToLeft && !upToDown) {
    deg = Math.abs(deg)
  }
  const angle = direction === LineDirection.LeftToRight ? deg : 180 - deg
  return {
    point: best!.point,
    distance: Math.sqrt(best!.dist2),
    segmentIndex: bestIndex,
    t: best!.t,
    angle: angle,
    direction
  }
}